import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CopyStopsIcon from '../../../assets/svg/route-actions-menu/CopyStopsIcon';
import DeleteStopsIcon from '../../../assets/svg/route-actions-menu/DeleteStopsIcon';
import { DriverProgress, DriverProgressItem } from '../../../components/DriversProgress';
import { FastActionsMenu } from '../../../components/FastActionsMenu';
import { Modal } from '../../../components/Modal';
import * as routing from '../../../constants/Routing';
import { DistanceType } from '../../../domain/driversProject/DriversProject';
import { Route } from '../../../domain/route/Route';
import { Stop } from '../../../domain/stops/Stop';
import { intl } from '../../../helpers/IntGlobalProvider';
import { Text } from '../../../react-components/Text';
import { RouteStatusBar } from '../components/StatusBar';
import { usePermissions } from '../hooks/usePermissions';
import { useRetrieveVehicle } from '../hooks/useRetrieveVehicle';
import { PRIVATE_COMPONENT_ID } from '../PrivateComponent';
import { useDeleteRoute } from '../route/hooks/useDeleteRoute';
import { RouteDuplication } from '../route/stops/route-stops-container-header/RouteDuplication';
import { RoutePillInfo } from './RoutePillInfo';

export const ROUTE_PILL_HEIGHT = 80;

interface RoutePillProps {
  route: Route;
  style?: React.CSSProperties;
  disabled?: boolean;
  onDelete?: () => void;
}

export const RoutePill = ({ route, style, disabled = false, onDelete }: RoutePillProps) => {
  const navigate = useNavigate();
  const { canManageRoutes, canManageStops } = usePermissions();
  const { data: vehicleData } = useRetrieveVehicle();
  const [isPressed, setIsPressed] = useState<boolean>(false);
  const [longPressTimeout, setLongPressTimeout] = useState<NodeJS.Timeout | null>(null);
  const [longPressTriggered, setLongPressTriggered] = useState<boolean>(false); // New state to track long press
  const [fastActionsOpen, setFastActionsOpen] = useState<boolean>(false);
  const [isViewingDuplicateStops, setIsViewingDuplicateStops] = useState<boolean>(false);
  const [deleteRouteDialogOpen, setDeleteRouteDialogOpen] = useState<boolean>(false);

  const { run: deleteRoute, isLoading: deleteRouteLoading, success: deleteRouteSuccess } = useDeleteRoute();

  useEffect(() => {
    if (deleteRouteSuccess) {
      setFastActionsOpen(false);
      onDelete && onDelete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteRouteSuccess]);

  useEffect(() => {
    if (disabled && longPressTimeout) {
      clearTimeout(longPressTimeout); // Clear timeout if onLongPress is not defined
      setLongPressTimeout(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const handleMouseDown = () => {
    setIsPressed(true);
    setLongPressTriggered(false); // Reset long press flag
    const timeout = setTimeout(() => {
      setLongPressTriggered(true); // Mark long press as triggered
      if (!disabled) {
        onLongPress();
      }
    }, 500); // 500ms for long press
    setLongPressTimeout(timeout);
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    setIsPressed(false);
    if (longPressTimeout) {
      clearTimeout(longPressTimeout); // Cancel long press if released early
      setLongPressTimeout(null);
    }
    if (!longPressTriggered && !disabled) {
      // Only fire onClick if long press was not triggered
      return onClick(event);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsPressed(true);
    setLongPressTriggered(false); // Reset long press flag
    e.stopPropagation();

    const timeout = setTimeout(() => {
      setLongPressTriggered(true); // Mark long press as triggered
      if (!disabled) {
        onLongPress();
      }
    }, 500); // 500ms for long press
    setLongPressTimeout(timeout);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    setIsPressed(false);
    if (longPressTimeout) {
      clearTimeout(longPressTimeout); // Cancel long press if released early
      setLongPressTimeout(null);
    }
    if (!longPressTriggered && !disabled) {
      return onClick(e);
    }
  };

  const onClick = (e: React.MouseEvent | React.TouchEvent) => {
    navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, route.id));
  };

  const onLongPress = () => {
    setIsPressed(false);
    if (canManageRoutes) {
      setFastActionsOpen(true);
    }
  };

  return (
    <>
      <RouteDuplication
        isOpen={isViewingDuplicateStops}
        route_id={route.id}
        zIndex={1006}
        onClose={() => setIsViewingDuplicateStops(false)}
        portalElementId={PRIVATE_COMPONENT_ID}
      />

      <Modal
        open={deleteRouteDialogOpen}
        portalElementId={PRIVATE_COMPONENT_ID}
        title={{
          intlId: `drivers.delete_route_warning.title`,
        }}
        subtitle={{
          intlId: `drivers.delete_route_warning.info`,
        }}
        isLoading={deleteRouteLoading}
        buttons={[
          {
            intlId: `confirm`,
            onClick: () => {
              deleteRoute({ route });
            },
            disabled: deleteRouteLoading,
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setDeleteRouteDialogOpen(false);
            },
            disabled: deleteRouteLoading,
          },
        ]}
      />

      <FastActionsMenu
        zIndex={1006}
        openMenu={fastActionsOpen}
        setOpenMenu={setFastActionsOpen}
        portalElementId={PRIVATE_COMPONENT_ID}
        actions={[
          ...(canManageStops
            ? [
                {
                  intlId: `drivers.route.actions_menu.duplicate_route`,
                  icon: <CopyStopsIcon />,
                  onClick: () => {
                    setIsViewingDuplicateStops(true);
                  },
                },
              ]
            : []),
          {
            intlId: `drivers.route.actions_menu.delete_route`,
            textStyles: { color: RoutalPalette.error.medium },
            icon: <DeleteStopsIcon />,
            onClick: () => {
              setDeleteRouteDialogOpen(true);
            },
          },
        ]}
      />

      <Grid
        key={route.id}
        container
        direction="row"
        wrap="nowrap"
        style={{
          position: `relative`,
          height: `${ROUTE_PILL_HEIGHT}px`,
          cursor: `pointer`,
          backgroundColor: !disabled && isPressed ? RoutalPalette.primary40n : RoutalPalette.white,
          padding: `8px 10px`,
          ...style,
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={(event) => handleMouseUp(event)}
        onMouseLeave={() => setIsPressed(false)} // Reset if mouse leaves the grid
        onTouchStart={handleTouchStart}
        onTouchEnd={(event) => handleTouchEnd(event)}
      >
        <RouteStatusBar status={route.status} />

        <HSpacer small />

        <Grid
          item
          container
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          wrap="nowrap"
          padding="8px 0px"
        >
          <Text style={{ color: route.plan_label ? undefined : RoutalPalette.neutral10 }} variant="body">
            {route.plan_label ?? intl.formatMessage({ id: `drivers.route_header.no_label` })}
          </Text>

          <RoutePillInfo
            route={route as Route}
            distanceType={vehicleData?.project.units.distance as DistanceType}
            color={RoutalPalette.secondary00}
          />

          <DriverProgress style={{ margin: 0 }} hasStops={Boolean(route.stops.length > 0)}>
            {route.stops.map((stop: Stop) => (
              <DriverProgressItem key={stop.id} status={stop.status} routeStatus={route.status} enableOpacity />
            ))}
          </DriverProgress>
        </Grid>
      </Grid>
    </>
  );
};
